import { formatMinor, type SignOffCandidate, type SignOffResult } from '@hvac/contracts';
import { Alert, Button, Skeleton } from '@hvac/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { bookkeepingKeys } from '../../shared/api/bookkeeping-queries.js';
import { closeKeys, signOffQuery, signOffRecords } from '../../shared/api/close-queries.js';

/**
 * Sign-off: the system prepares, a person signs.
 *
 * Everything listed here has nothing standing in its way, which the database
 * decided at the moment it was asked. The person reads the list, spot-checks
 * a few, and signs. What is signed is exactly what is on screen: the ids go
 * to the database, which asks each one again and skips any that stopped being
 * ready while this page was open, saying why.
 *
 * The spot check is the point of the design, not decoration. A batch
 * signature is only as good as the evidence that the batch is right, and
 * three records chosen at random are the cheapest evidence there is.
 */

const SPOT_CHECK = 3;

export function SignOffPanel({
  organizationId,
  periodId,
}: {
  organizationId: string;
  periodId: string;
}) {
  const queryClient = useQueryClient();
  const candidates = useQuery(signOffQuery(organizationId, periodId));
  const [result, setResult] = useState<SignOffResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const rows = useMemo(() => candidates.data ?? [], [candidates.data]);
  // Drawn once per list, so the sample does not reshuffle while it is being read.
  const sample = useMemo(() => pickSample(rows, SPOT_CHECK), [rows]);

  const sign = useMutation({
    mutationFn: () =>
      signOffRecords(
        organizationId,
        periodId,
        rows.map((row) => row.transactionId),
      ),
    onSuccess: (outcome) => {
      setError(null);
      setResult(outcome);
      void queryClient.invalidateQueries({ queryKey: closeKeys.all(organizationId) });
      void queryClient.invalidateQueries({ queryKey: bookkeepingKeys.all(organizationId) });
    },
    onError: (caught) =>
      setError(caught instanceof Error ? caught.message : 'Those records could not be signed.'),
  });

  if (candidates.isPending) return <Skeleton className="h-24 w-full" />;

  const total = rows.reduce((sum, row) => sum + BigInt(row.grossAmountMinor), 0n);
  const credits = rows.reduce((sum, row) => sum + BigInt(row.itcAmountMinor), 0n);

  return (
    <div className="flex flex-col gap-3 rounded-[var(--radius-control)] border border-border bg-surface-sunken px-4 py-3">
      {error === null ? null : (
        <Alert tone="danger" title="Nothing was signed">
          {error}
        </Alert>
      )}

      {result === null ? null : (
        <Alert
          tone={result.skipped.length === 0 ? 'success' : 'warning'}
          title={`Signed ${String(result.signed)}`}
        >
          {result.skipped.length === 0
            ? 'Every record on the list is now in the books.'
            : `${String(result.skipped.length)} changed while this page was open and were left as drafts: ${result.skipped
                .map((skip) => skip.reasons.join(' '))
                .slice(0, 3)
                .join(' · ')}`}
        </Alert>
      )}

      {rows.length === 0 ? (
        <p className="text-sm text-content-muted">
          Nothing is waiting for a signature in this month.
        </p>
      ) : (
        <>
          <p className="text-sm text-content">
            {rows.length} record{rows.length === 1 ? '' : 's'}, {formatMinor(total.toString())}{' '}
            spent, {formatMinor(credits.toString())} of GST/HST credits. Each has a known supplier,
            an account, a receipt photographed within days, and no warnings.
          </p>

          {sample.length === 0 ? null : (
            <div className="flex flex-col gap-1">
              <p className="text-xs font-medium uppercase tracking-wide text-content-muted">
                Spot-check these first
              </p>
              <ul className="flex flex-col gap-1 text-sm">
                {sample.map((row) => (
                  <li key={row.transactionId}>
                    <Link
                      to="/bookkeeping/$recordId"
                      params={{ recordId: row.transactionId }}
                      className="text-brand-600 underline hover:text-brand-700"
                    >
                      {describe(row)}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <details className="text-sm">
            <summary className="cursor-pointer text-content-muted">See all {rows.length}</summary>
            <ul className="mt-2 flex max-h-72 flex-col gap-1 overflow-y-auto">
              {rows.map((row) => (
                <li key={row.transactionId}>
                  <Link
                    to="/bookkeeping/$recordId"
                    params={{ recordId: row.transactionId }}
                    className="hover:underline"
                  >
                    {describe(row)}
                  </Link>
                </li>
              ))}
            </ul>
          </details>

          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={() => sign.mutate()} isLoading={sign.isPending}>
              Sign {rows.length} record{rows.length === 1 ? '' : 's'}
            </Button>
            <span className="text-xs text-content-muted">
              Signing puts them in the books. It does not export or file anything.
            </span>
          </div>
        </>
      )}
    </div>
  );
}

function describe(row: SignOffCandidate): string {
  return [
    row.transactionDate,
    row.supplier ?? 'No supplier',
    row.category ?? 'No account',
    formatMinor(row.grossAmountMinor),
  ].join(' · ');
}

/** A random few, so the check cannot drift toward the ones that look fine. */
function pickSample(rows: SignOffCandidate[], size: number): SignOffCandidate[] {
  const pool = [...rows];
  for (let i = pool.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    const held = pool[i];
    const swapped = pool[j];
    if (held === undefined || swapped === undefined) continue;
    pool[i] = swapped;
    pool[j] = held;
  }
  return pool.slice(0, size);
}
